import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Button, Typography, Alert, Divider,
} from '@mui/material';
import { Google } from '@mui/icons-material';
import useAuth from '../hooks/useAuth';
import gblLogo from '../images/gbl logo.png';

const LoginPage = () => {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleGoogleSignIn = async () => {
    setError('');
    setLoading(true);

    try {
      const user = await login();
      if (user.role === 'banker') {
        navigate('/banker-portal');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      const status = err.response?.status;
      const msg = err.response?.data?.message || err.message || 'Login failed';
      if (status === 403 && msg.includes('not registered')) {
        setError('Access denied. Your email is not registered in the system. Please contact the administrator to get access.');
      } else if (status === 403) {
        setError(msg);
      } else if (msg.includes('auth/popup-closed-by-user')) {
        setError('Sign-in was cancelled. Please try again.');
      } else if (msg.includes('auth/popup-blocked')) {
        setError('Pop-up was blocked by your browser. Please allow pop-ups and try again.');
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#F8F9FA',
        p: 2,
      }}
    >
      <Card
        sx={{
          maxWidth: 400,
          width: '100%',
          borderRadius: 2,
          border: '1px solid #DADCE0',
          boxShadow: '0 1px 2px 0 rgba(60,64,67,0.3), 0 2px 6px 2px rgba(60,64,67,0.15)',
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Box
              component="img"
              src={gblLogo}
              alt="GBL Tree - Abroad Education Loans"
              sx={{
                height: 64,
                width: 'auto',
                objectFit: 'contain',
                mx: 'auto',
                mb: 2,
                display: 'block',
              }}
            />
            <Typography sx={{ fontWeight: 400, fontSize: '1.5rem', color: '#202124', mb: 0.5 }}>
              GBL Tree CRM
            </Typography>
            <Typography sx={{ fontSize: '0.875rem', color: '#5F6368' }}>
              Abroad Education Loan Management
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: 1 }}>
              {error}
            </Alert>
          )}

          <Divider sx={{ mb: 3 }}>
            <Typography sx={{ fontSize: '0.75rem', color: '#5F6368', fontWeight: 400 }}>
              Sign in to continue
            </Typography>
          </Divider>

          <Button
            fullWidth
            variant="outlined"
            size="large"
            startIcon={<Google />}
            onClick={handleGoogleSignIn}
            disabled={loading}
            sx={{
              py: 1.5,
              borderColor: '#DADCE0',
              color: '#202124',
              fontSize: '0.875rem',
              fontWeight: 500,
              borderRadius: 1,
              '&:hover': {
                borderColor: '#1A73E8',
                backgroundColor: 'rgba(26,115,232,0.04)',
              },
            }}
          >
            {loading ? 'Signing in...' : 'Sign in with Google'}
          </Button>

          <Typography
            sx={{
              display: 'block',
              textAlign: 'center',
              mt: 3,
              fontSize: '0.75rem',
              color: '#9AA0A6',
              lineHeight: 1.6,
            }}
          >
            Only authorized users can access this system.
            <br />
            Contact your administrator for access.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default LoginPage;
